import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Conversation } from '../utils/types';
import { initialConversations } from '../utils/sampleData';
import Sidebar from './Sidebar';
import ChatHeader from './ChatHeader';
import MessageArea from './MessageArea';
import InputArea from './InputArea';
import WelcomeScreen from './WelcomeScreen';
import { useChat } from '../hooks/useChat';
import chatApiService from '../services/api';
import { useChatApi } from '../contexts/ChatApiContext';

const LOCAL_STORAGE_KEY = 'technicalSupportChatbot_conversations';

interface TechnicalSupportChatbotProps {
    initialConversationId?: number;
    forceConnected?: boolean;
}

const TechnicalSupportChatbot: React.FC<TechnicalSupportChatbotProps> = ({
    initialConversationId,
    forceConnected = false
}) => {
    const navigate = useNavigate();
    const { isConnected: contextIsConnected, connect: contextConnect } = useChatApi();

    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [activeConversation, setActiveConversation] = useState<number | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [sidebarSearchQuery, setSidebarSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState<Array<{
        conversationId: number;
        messageId: number;
        content: string;
    }>>([]);
    const [showSearchResults, setShowSearchResults] = useState(false);
    const [conversations, setConversations] = useState<Conversation[]>([]);
    const [newMessage, setNewMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [submitDisabled, setSubmitDisabled] = useState(false);
    const initialLoadComplete = useRef(false);

    const shouldScrollToBottom = useRef(false);

    const { sendMessage: sendChatMessage, isConnected, isWaiting } = useChat(
        conversations,
        setConversations,
        activeConversation
    );

    useEffect(() => {
        setIsLoading(isWaiting);
        setSubmitDisabled(isWaiting || !isConnected);

        if (isWaiting) {
            shouldScrollToBottom.current = true;
        }
    }, [isWaiting, isConnected]);

    useEffect(() => {
        const savedConversations = localStorage.getItem(LOCAL_STORAGE_KEY);
        if (savedConversations) {
            try {
                setConversations(JSON.parse(savedConversations));
            } catch (error) {
                console.error('Error parsing conversations from localStorage:', error);
                setConversations(initialConversations);
            }
        } else {
            setConversations(initialConversations);
        }
    }, []);

    useEffect(() => {
        if (conversations.length > 0 && initialConversationId && !initialLoadComplete.current) {
            const index = conversations.findIndex(conv => conv.id === initialConversationId);

            if (index !== -1) {
                setActiveConversation(index);
                setSidebarOpen(false);
            } else {
                console.error(`Conversation with ID ${initialConversationId} not found.`);
            }

            initialLoadComplete.current = true;
        }
    }, [initialConversationId, conversations]);

    useEffect(() => {
        if (conversations.length > 0) {
            localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(conversations));
        }
    }, [conversations]);

    useEffect(() => {
        if (shouldScrollToBottom.current) {
            requestAnimationFrame(() => {
                const messageContainer = document.querySelector('.message-area');
                if (messageContainer) {
                    messageContainer.scrollTop = messageContainer.scrollHeight;
                }
                shouldScrollToBottom.current = false;
            });
        }
    }, [conversations]);

    const toggleSidebar = () => {
        setSidebarOpen(!sidebarOpen);
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const query = e.target.value;
        setSearchQuery(query);

        if (query.length > 2) {
            const results: Array<{
                conversationId: number;
                messageId: number;
                content: string;
            }> = [];

            conversations.forEach(conv => {
                conv.messages.forEach(msg => {
                    if (msg.type === "question" && msg.content.toLowerCase().includes(query.toLowerCase())) {
                        results.push({
                            conversationId: conv.id,
                            messageId: msg.id,
                            content: msg.content
                        });
                    }
                });
            });

            setSearchResults(results);
            setShowSearchResults(true);
        } else {
            setShowSearchResults(false);
        }
    };

    const handleSidebarSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const query = e.target.value;
        setSidebarSearchQuery(query);
    };

    const selectConversation = (id: number) => {
        const index = conversations.findIndex(conv => conv.id === id);
        if (index !== -1) {
            setActiveConversation(index);
            navigate(`/alex/${id}`);

            if (window.innerWidth < 768) {
                setSidebarOpen(false);
            }
        } else {
            console.error(`Conversation with id ${id} not found`);
        }
    };

    const closeConversation = () => {
        setActiveConversation(null);
        setSidebarOpen(false);
        navigate('/alex');
    };

    const createNewConversation = () => {
        const newId = conversations.length > 0
            ? Math.max(...conversations.map(c => c.id)) + 1
            : 1;

        const newConversation: Conversation = {
            id: newId,
            title: `New Conversation ${newId}`,
            messages: []
        };

        const updatedConversations = [...conversations, newConversation];
        setConversations(updatedConversations);
        setActiveConversation(updatedConversations.length - 1);
        setSidebarOpen(true);
        navigate(`/alex/${newId}`);
    };

    const toggleMessageExpansion = (conversationId: number, messageId: number) => {
        setConversations(conversations.map(conversation => {
            if (conversation.id === conversationId) {
                return {
                    ...conversation,
                    messages: conversation.messages.map(message => {
                        if (message.id === messageId) {
                            return { ...message, expanded: !message.expanded };
                        }
                        return message;
                    })
                };
            }
            return conversation;
        }));
    };

    const editConversationTitle = (id: number, newTitle: string) => {
        const updatedConversations = conversations.map(conv => {
            if (conv.id === id) {
                return { ...conv, title: newTitle };
            }
            return conv;
        });
        setConversations(updatedConversations);
    };

    const deleteConversation = (id: number) => {
        const deleteIndex = conversations.findIndex(conv => conv.id === id);

        const updatedConversations = conversations.filter(conv => conv.id !== id);

        if (activeConversation !== null) {
            if (activeConversation === deleteIndex) {
                setActiveConversation(null);
                navigate('/alex');
            } else if (deleteIndex < activeConversation) {
                setActiveConversation(activeConversation - 1);
            }
        }

        setConversations(updatedConversations);
    };

    const handleNewMessageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setNewMessage(e.target.value);
    };

    const handleMessageInConversation = (conversationIndex: number, message: string) => {
        if (conversationIndex < 0 || conversationIndex >= conversations.length) {
            console.error(`Conversation at index ${conversationIndex} does not exist`);
            setActiveConversation(null);
            setTimeout(() => {
                if (message) {
                    setNewMessage(message);
                    setTimeout(() => sendMessage(), 0);
                }
            }, 0);
            return;
        }

        // Check connection first
        if (!isConnected) {
            console.error("Cannot send message: WebSocket is not connected");
            alert("Cannot send message: WebSocket is not connected. Please go back to home page and connect first.");
            return;
        }

        const currentConversation = conversations[conversationIndex];

        console.log("Sending message to API:", message, "to conversation ID:", currentConversation.id);

        sendChatMessage(message, currentConversation.id);

        setNewMessage('');
    };

    const sendMessage = () => {
        if (newMessage.trim() === '' || submitDisabled) return;

        if (!isConnected && !forceConnected) {
            console.error("Cannot send message: WebSocket is not connected");

            alert("Attempting to connect to the AI service. Please try again in a moment.");
            contextConnect();
            return;
        }

        console.log("sendMessage called with:", {
            newMessage,
            isConnected,
            forceConnected,
            activeConversation,
            conversationsCount: conversations.length
        });

        if (activeConversation === null || conversations.length === 0) {
            const newId = conversations.length > 0
                ? Math.max(...conversations.map(c => c.id)) + 1
                : 1;

            const timestamp = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

            const newMessageObj = {
                id: 1,
                groupId: 1,
                type: "question" as const,
                content: newMessage,
                timestamp: timestamp
            };

            const newConversation: Conversation = {
                id: newId,
                title: newMessage.length > 30 ? `${newMessage.substring(0, 30)}...` : newMessage,
                messages: [newMessageObj]
            };

            const updatedConversations = [...conversations, newConversation];

            console.log(`Creating new conversation with id ${newId} and first message: "${newMessage}"`);

            const currentMessage = newMessage;

            setNewMessage('');

            setConversations(updatedConversations);

            setTimeout(() => {
                console.log(`Setting active conversation to ${updatedConversations.length - 1} with ID: ${newId}`);

                setActiveConversation(updatedConversations.length - 1);

                navigate(`/alex/${newId}`);

                if (isConnected) {
                    console.log(`Ready to send first message to API: "${currentMessage}" for conversation ID: ${newId}`);

                    setTimeout(() => {
                        console.log("Sending first message directly to API to avoid duplication");
                        chatApiService.sendMessage(currentMessage, newId);
                        setIsLoading(true);
                    }, 300);
                } else {
                    console.error("Connection lost before sending message");

                    const systemMessage = {
                        id: 2,
                        groupId: 1,
                        type: "answer" as const,
                        content: "Connection to AI service lost. Please go back to home page and reconnect.",
                        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                        expanded: true
                    };

                    setConversations(prevConversations => {
                        const updated = [...prevConversations];
                        const lastIndex = updated.length - 1;
                        if (lastIndex >= 0) {
                            updated[lastIndex].messages.push(systemMessage);
                        }
                        return updated;
                    });
                }
            }, 100);

            return;
        }

        if (activeConversation < 0 || activeConversation >= conversations.length) {
            console.error(`Invalid active conversation index: ${activeConversation}`);
            setActiveConversation(null);
            setTimeout(() => sendMessage(), 0);
            return;
        }

        handleMessageInConversation(activeConversation, newMessage);
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !submitDisabled) {
            sendMessage();
        }
    };

    const currentConversation = activeConversation !== null && activeConversation < conversations.length
        ? conversations[activeConversation]
        : null;

    const handleSearchResultClick = (conversationId: number, messageId: number) => {
        const targetIndex = conversations.findIndex(conv => conv.id === conversationId);
        if (targetIndex !== -1) {
            setActiveConversation(targetIndex);
            navigate(`/alex/${conversationId}`);
        }

        setShowSearchResults(false);
        setSearchQuery('');

        setTimeout(() => {
            const element = document.getElementById(`question-${messageId}`);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                element.classList.add('bg-yellow-50');
                setTimeout(() => {
                    element.classList.remove('bg-yellow-50');
                }, 2000);
            }
        }, 100);
    };

    return (
        <div className="flex h-screen bg-white font-sans">
            <div className="grid grid-cols-12 w-full">
                <div className={`${sidebarOpen ? 'col-span-3' : 'col-span-0 hidden'} transition-all duration-300 md:block`}>
                    <Sidebar
                        conversations={conversations}
                        activeConversation={activeConversation !== null ? activeConversation : -1}
                        searchQuery={sidebarSearchQuery}
                        onSearchChange={handleSidebarSearchChange}
                        onConversationSelect={selectConversation}
                        onNewConversation={createNewConversation}
                        onEditConversation={editConversationTitle}
                        onDeleteConversation={deleteConversation}
                        isOpen={sidebarOpen}
                    />
                </div>

                <div className={`${sidebarOpen ? 'col-span-9' : 'col-span-12'} flex flex-col h-screen transition-all duration-300`}>
                    <ChatHeader
                        conversation={currentConversation}
                        sidebarOpen={sidebarOpen}
                        toggleSidebar={toggleSidebar}
                        searchQuery={searchQuery}
                        searchResults={searchResults}
                        showSearchResults={showSearchResults}
                        onSearchChange={handleSearchChange}
                        onSearchResultClick={handleSearchResultClick}
                        conversations={conversations}
                        onCloseConversation={currentConversation ? closeConversation : undefined}
                    />

                    <div className="message-area flex-1 overflow-y-auto">

                        {currentConversation ? (
                            <MessageArea
                                conversation={currentConversation}
                                toggleMessageExpansion={toggleMessageExpansion}
                                isLoading={isLoading}
                            />
                        ) : (
                            <WelcomeScreen
                                newMessage={newMessage}
                                onNewMessageChange={handleNewMessageChange}
                                onKeyPress={handleKeyPress}
                                onSend={sendMessage}
                                disabled={submitDisabled}
                                isConnected={isConnected}
                            />
                        )}
                    </div>

                    <div className="sticky bottom-0 z-10 px-4 pb-4 pt-2 bg-white/80 backdrop-blur-sm">
                        <div className="max-w-4xl mx-auto">
                            <InputArea
                                value={newMessage}
                                onChange={handleNewMessageChange}
                                onKeyPress={handleKeyPress}
                                onSend={sendMessage}
                                disabled={submitDisabled}
                                isConnected={isConnected}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TechnicalSupportChatbot;